import React, { useMemo } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

/**
 * Compact rich-text editor for daily-update fields (Completed / In Progress / Notes).
 * Stores HTML. Use the static helper `RichEditor.isEmpty(html)` to detect empty content
 * (Quill produces "<p><br></p>" for empty state — that should not count as filled).
 */
export default function RichEditor({ value, onChange, placeholder, accentColor = '#0984e3', minHeight = 96 }) {
  const modules = useMemo(() => ({
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link', 'blockquote', 'code-block'],
      ['clean'],
    ],
    clipboard: { matchVisual: false },
  }), []);

  const formats = [
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'link', 'blockquote', 'code-block',
  ];

  return (
    <div
      className="rich-editor rounded-xl border border-[#E5E7EB] bg-white overflow-hidden focus-within:ring-2 transition-shadow"
      style={{ '--accent': accentColor }}
    >
      <ReactQuill
        theme="snow"
        value={value || ''}
        onChange={onChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
      />
      <style>{`
        .rich-editor .ql-toolbar {
          border: none !important;
          border-bottom: 1px solid #F3F4F6 !important;
          padding: 6px 8px !important;
          background: #FAFBFC;
        }
        .rich-editor .ql-container {
          border: none !important;
          font-size: 13px;
          font-family: inherit;
        }
        .rich-editor .ql-editor {
          min-height: ${minHeight}px;
          padding: 10px 14px;
          color: #1F2937;
        }
        .rich-editor .ql-editor.ql-blank::before {
          color: #9CA3AF;
          font-style: normal;
          left: 14px;
          right: 14px;
        }
        .rich-editor:focus-within {
          border-color: ${accentColor};
          box-shadow: 0 0 0 3px ${accentColor}1a;
        }
        .rich-editor .ql-snow .ql-stroke { stroke: #6B7280; }
        .rich-editor .ql-snow .ql-fill { fill: #6B7280; }
        .rich-editor .ql-snow button:hover .ql-stroke,
        .rich-editor .ql-snow .ql-active .ql-stroke { stroke: ${accentColor}; }
        .rich-editor .ql-snow button:hover .ql-fill,
        .rich-editor .ql-snow .ql-active .ql-fill { fill: ${accentColor}; }
        .rich-editor .ql-snow .ql-picker-label { color: #6B7280; }
      `}</style>
    </div>
  );
}

/**
 * Returns true if the HTML produced by Quill represents empty content.
 * Quill outputs "<p><br></p>" for an empty editor, so we strip tags and whitespace.
 */
RichEditor.isEmpty = function isEmpty(html) {
  if (!html) return true;
  const stripped = String(html).replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
  return stripped.length === 0;
};
